import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import JsonBlock from './JsonBlock';
import StatusBadge from './StatusBadge';
import SummaryCard from './SummaryCard';

const RESULT_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const prettifySource = (value) => {
  if (!value) return null;
  return String(value).replaceAll('_', ' ');
};

const prettifyTagReason = (reason) => {
  if (!reason) return null;

  if (reason === 'existing tags preserved') return 'existing tags preserved';
  if (reason === 'description yok') return 'missing description';
  if (reason.startsWith('description cok kisa')) return 'description too short';
  if (reason === 'tags disabled') return 'tags disabled';

  return reason;
};

const getPrimaryResult = (item) => {
  if (item.status === 'failed') {
    if (item.action === 'validation_error') {
      return { label: 'Failed (validation error)', tone: 'red' };
    }

    return { label: 'Failed (system error)', tone: 'red' };
  }

  if (item.status === 'skipped') {
    if (item.action === 'duplicate') {
      return { label: 'Skipped (duplicate match found)', tone: 'yellow' };
    }

    return { label: 'Skipped', tone: 'yellow' };
  }

  if (item.action === 'inserted') {
    return { label: 'Inserted (new book created)', tone: 'green' };
  }

  if (item.action === 'updated') {
    return { label: 'Updated (Excel override applied)', tone: 'blue' };
  }

  if (item.action === 'would_insert') {
    return { label: 'Would insert (new book would be created)', tone: 'blue' };
  }

  if (item.action === 'would_update') {
    return { label: 'Would update (Excel override would be applied)', tone: 'blue' };
  }

  return { label: 'Processed', tone: 'gray' };
};

const getEnrichmentState = (item) => {
  const meta = item.resultMeta || {};

  if (item.status === 'skipped' && item.action === 'duplicate') {
    return { label: 'Enrichment not needed', tone: 'gray' };
  }

  if (meta.enrichment_error) {
    return { label: 'Enrichment failed', tone: 'red' };
  }

  const fillSignals = [];

  if (meta.cover_url_updated) fillSignals.push('cover');
  if (meta.reading_age_source && meta.reading_age_source !== 'input') fillSignals.push('reading age');
  if (meta.illustrator_source && meta.illustrator_source !== 'input') fillSignals.push('illustrator');

  if (fillSignals.length > 1) {
    return { label: 'Enriched', tone: 'green' };
  }

  if (fillSignals.length === 1) {
    return { label: 'Partially enriched', tone: 'yellow' };
  }

  return { label: 'Enrichment not needed', tone: 'gray' };
};

const getTagState = (item) => {
  const meta = item.resultMeta || {};

  if (meta.tag_error) {
    return { label: `Tags failed (${meta.tag_error})`, tone: 'red' };
  }

  if (meta.tags_generated) {
    return {
      label: meta.tags_count
        ? `Tags generated (${meta.tags_count})`
        : 'Tags generated',
      tone: 'green',
    };
  }

  if (meta.tag_skip_reason) {
    return { label: `Tags skipped (${prettifyTagReason(meta.tag_skip_reason)})`, tone: 'yellow' };
  }

  return null;
};

const lineToneClasses = {
  green: 'text-green-700',
  yellow: 'text-yellow-700',
  blue: 'text-blue-700',
  red: 'text-red-700',
  gray: 'text-text-secondary',
};

const buildMetaHighlights = (item) => {
  const meta = item.resultMeta || {};
  const highlights = [];

  if (meta.google_books_found === true) {
    highlights.push({ label: 'Google Books', value: 'Found', tone: 'blue' });
  } else if (meta.google_books_found === false) {
    highlights.push({ label: 'Google Books', value: 'Not found', tone: 'gray' });
  }

  if (meta.tags_generated === true) {
    highlights.push({ label: 'AI Tags', value: `${meta.tags_count || 0} generated`, tone: 'green' });
  } else if (meta.tag_skip_reason) {
    highlights.push({ label: 'AI Tags', value: meta.tag_skip_reason, tone: 'yellow' });
  }

  if (meta.reading_age_source) {
    highlights.push({ label: 'Reading Age', value: prettifySource(meta.reading_age_source), tone: 'purple' });
  }

  if (meta.illustrator_source) {
    highlights.push({ label: 'Illustrator', value: prettifySource(meta.illustrator_source), tone: 'purple' });
  }

  if (meta.existing_book_id) {
    highlights.push({ label: 'Existing Book', value: `#${meta.existing_book_id}`, tone: 'yellow' });
  }

  return highlights;
};

const toneClasses = {
  green: 'bg-green-50 text-green-700 border-green-200',
  yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  purple: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200',
};

const ResultsStep = ({
  activeJobStatus,
  canRetry,
  resetFlow,
  handleRetryFailed,
  retryLoading,
  resultFilter,
  setResultFilter,
  resultsLoading,
  resultsData,
}) => {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-bg-surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-text-primary">Import results</h2>
              {activeJobStatus?.status && <StatusBadge status={activeJobStatus.status} />}
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              Review the final outcome and retry failed rows if needed.
            </p>
          </div>
          <button
            onClick={resetFlow}
            className="rounded-lg border border-gray-200 px-4 py-2 text-text-primary hover:bg-gray-50"
          >
            New Import
          </button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <SummaryCard label="Processed" value={activeJobStatus?.counts?.processed || 0} />
          <SummaryCard label="Success" value={activeJobStatus?.counts?.success || 0} />
          <SummaryCard label="Failed" value={activeJobStatus?.counts?.failed || 0} />
          <SummaryCard label="Skipped" value={activeJobStatus?.counts?.skipped || 0} />
        </div>
      </div>

      {canRetry && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-medium text-yellow-800">Some rows failed</div>
              <div className="text-sm text-yellow-800">Retry will reuse the same job and reprocess failed rows only.</div>
            </div>
            <button
              onClick={handleRetryFailed}
              disabled={retryLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {retryLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Retry Failed Rows
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-bg-surface p-6">
        <div className="flex flex-wrap gap-2">
          {RESULT_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setResultFilter(filter.value)}
              className={`rounded-full px-3 py-1 text-sm ${
                resultFilter === filter.value
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'bg-gray-100 text-text-secondary'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {resultsLoading ? (
            <div className="flex items-center gap-2 text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading results...
            </div>
          ) : resultsData?.items?.length ? (
            <div className="space-y-4">
              {resultsData.items.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 p-4">
                  {(() => {
                    const primaryResult = getPrimaryResult(item);
                    const enrichmentState = getEnrichmentState(item);
                    const tagState = getTagState(item);

                    return (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">Row {item.rowNumber}</span>
                        <StatusBadge status={item.status} />
                      </div>
                      <div className={`mt-2 text-sm font-medium ${lineToneClasses[primaryResult.tone] || lineToneClasses.gray}`}>
                        {primaryResult.label}
                        {item.bookId ? ` • Book ID: ${item.bookId}` : ''}
                      </div>
                      <div className="mt-2 space-y-1 text-sm">
                        {enrichmentState && (
                          <div className={lineToneClasses[enrichmentState.tone] || lineToneClasses.gray}>
                            {enrichmentState.label}
                          </div>
                        )}
                        {tagState && (
                          <div className={lineToneClasses[tagState.tone] || lineToneClasses.gray}>
                            {tagState.label}
                          </div>
                        )}
                      </div>
                    </div>

                    {item.status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                    {item.status === 'failed' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                  </div>
                    );
                  })()}

                  {item.errorMessage && (
                    <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                      {item.errorMessage}
                    </div>
                  )}

                  {buildMetaHighlights(item).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {buildMetaHighlights(item).map((highlight, index) => (
                        <div
                          key={`${highlight.label}-${highlight.value}-${index}`}
                          className={`rounded-full border px-3 py-1 text-xs font-medium ${toneClasses[highlight.tone] || toneClasses.gray}`}
                        >
                          {highlight.label}: {highlight.value}
                        </div>
                      ))}
                    </div>
                  )}

                  {item.resultMeta && Object.keys(item.resultMeta).length > 0 && (
                    <details className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-text-secondary">
                        Technical Result Meta
                      </summary>
                      <div className="mt-3">
                        <JsonBlock value={item.resultMeta} />
                      </div>
                    </details>
                  )}

                  {item.inputPayload && (
                    <details className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                      <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-red-700">
                        Failed Input Payload
                      </summary>
                      <div className="mt-3">
                        <JsonBlock value={item.inputPayload} />
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-text-secondary">
              No items found for this filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsStep;
